// Багшийн порталын нийтлэг: API, зураг/аватар, ирцийн төлөвийн өнгө, хэсгийн гарчиг.
import React, { useEffect, useState } from "react";
import { message } from "antd";
import { instance } from "utils/axios";

export const api = (method, url, data) => instance({ method, url, ...(method === "get" ? { params: data } : { data }), noMessage: true }).then((r) => r.data);
export const errText = (e) => e?.response?.data?.error || e?.response?.data?.value || "Алдаа гарлаа";
const CDN = process.env.REACT_APP_CDN_URL || "";
export const fileUrl = (p) => (!p ? null : /^https?:/.test(p) ? p : `${CDN}${p.startsWith("/") ? "" : "/"}${p}`);

// Ажилтан/сурагчийн зураг (API CDN → олдохгүй бол production сервер), эс бөгөөс нэрийн эхний үсэг
const PROD = "https://api.tomujin.edu.mn";
export function Avatar({ user, size = 36, tone = "yellow" }) {
  const [step, setStep] = useState(0);
  const p = user?.photo;
  const srcs = !p ? [] : /^https?:/.test(p) ? [p] : [fileUrl(p), ...(CDN === PROD ? [] : [`${PROD}${p.startsWith("/") ? "" : "/"}${p}`])];
  const ini = `${user?.lastName?.[0] || ""}${user?.firstName?.[0] || ""}`.toUpperCase();
  // DS Avatar: muted дэвсгэр, muted-foreground текст, 600
  const bg = tone === "yellow" ? "bg-[#f2cd30] text-[#3d1b6e]" : "bg-[#f3eeff] text-[#6e5e8a]";
  if (step < srcs.length) return <img key={srcs[step]} src={srcs[step]} alt="" referrerPolicy="no-referrer" onError={() => setStep((x) => x + 1)} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  return <span style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }} className={`rounded-full ${bg} font-semibold flex items-center justify-center shrink-0`} aria-hidden="true">{ini}</span>;
}

export const STATUS_STYLE = {
  Ирсэн: { fg: "#166534", bg: "#dcfce7" },
  Хоцорсон: { fg: "#854d0e", bg: "#fef3c7" },
  Тасалсан: { fg: "#991b1b", bg: "#fee2e2" },
  Чөлөөтэй: { fg: "#1e40af", bg: "#dbeafe" },
  Өвчтэй: { fg: "#6b21a8", bg: "#f3e8ff" },
};

export const SectionTitle = ({ children, right }) => (
  <div className="flex justify-between items-baseline gap-3 mb-3">
    <h2 className="m-0 text-[15px] font-extrabold text-[#5c1090] uppercase tracking-wide">{children}</h2>
    {right}
  </div>
);
export const Card = ({ className = "", children }) => <section className={`bg-white border border-[#e5d8f5] rounded-2xl p-5 ${className}`}>{children}</section>;
export const pct = (v) => (v == null ? "—" : `${Math.round(v)}%`);
// URL-д ID биш уншигдахуйц slug ("10a-phys")
export const classPath = (c) => `/t/class/${c.slug}`;

// ── Кэш (stale-while-revalidate): цэс хооронд шилжихэд өмнө татсан өгөгдлийг шууд харуулж, арын горимд шинэчилнэ ──
const CACHE = new Map();
const keyOf = (url, params) => `${url}?${JSON.stringify(params || {})}`;
const inflight = new Map();
// Нэг хүсэлтийг давхар илгээхгүй (prefetch ба хуудас зэрэг)
export function fetchCached(url, params) {
  const k = keyOf(url, params);
  if (inflight.has(k)) return inflight.get(k);
  const p = api("get", url, params).then((d) => { CACHE.set(k, d); return d; }).finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}
export const prefetch = (url, params) => { if (!CACHE.has(keyOf(url, params))) fetchCached(url, params).catch(() => {}); };
// { data, setData (кэшийг ч шинэчилнэ), reload, refreshing }
export function useCached(url, params) {
  const k = url ? keyOf(url, params) : null;
  const [data, setState] = useState(() => (k ? CACHE.get(k) ?? null : null));
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!k) return undefined;
    let alive = true;
    setState(CACHE.get(k) ?? null);
    setRefreshing(true);
    fetchCached(url, params)
      .then((d) => { if (alive) setState(d); })
      .catch((e) => { if (alive && !CACHE.has(k)) message.error(errText(e)); })
      .finally(() => { if (alive) setRefreshing(false); });
    return () => { alive = false; };
  }, [k, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  // Хөтчийн цонх руу буцаж ороход (30с-ээс удаан байсан бол) дахин шинэчилнэ — бусдын өөрчлөлт харагдана
  useEffect(() => {
    if (!k) return undefined;
    let away = 0;
    const onHide = () => { if (document.visibilityState === "hidden") away = Date.now(); else if (away && Date.now() - away > 30000) setTick((t) => t + 1); };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [k]);
  // AI туслахаар баталсан өөрчлөлт → багшийн өгөгдлийг шууд дахин татна
  useEffect(() => {
    if (!k || !url.startsWith("/teacher")) return undefined;
    const h = () => { CACHE.delete(k); setTick((t) => t + 1); };
    window.addEventListener("ai:changed", h);
    return () => window.removeEventListener("ai:changed", h);
  }, [k]); // eslint-disable-line react-hooks/exhaustive-deps
  const setData = (v) => setState((prev) => { const n = typeof v === "function" ? v(prev) : v; if (k) CACHE.set(k, n); return n; });
  return { data, setData, reload: () => { if (k) CACHE.delete(k); setTick((t) => t + 1); }, refreshing };
}

// Арын горимд шинэчилж байх үед баруун дээд буланд жижиг тэмдэг
export const Refreshing = ({ on }) => (on ? <span role="status" className="fixed top-[70px] right-4 z-50 text-xs text-[#6e5e8a] bg-white border border-[#e5d8f5] rounded-full px-3 py-1 shadow-sm">Шинэчилж байна…</span> : null);

// СҮД-үүдийг бүлгээр (lms_unit) — дараалал серверээс бүлгээр ирдэг
export const byUnit = (outcomes) => outcomes.reduce((gs, o) => {
  const k = o.unitId ?? o.topic;
  const g = gs[gs.length - 1];
  if (g && g.key === k) g.items.push(o); else gs.push({ key: k, unitId: o.unitId, topic: o.topic, items: [o] });
  return gs;
}, []);
